import { z } from 'zod';
import { isoDateTime } from './common';

// ──────────────────────────────────────────────────────────────────────────
// Lifecycle enums
// ──────────────────────────────────────────────────────────────────────────
export const inscriptionStatusSchema = z.enum([
  'pending_contact',      // fresh signup — admin hasn't reached out yet
  'pending_confirmation', // admin contacted; awaiting confirmation from candidate
  'confirmed',            // candidate confirmed; admin can assign group + cohort
  'enrolled',             // payment recorded; participant is locked in
  'cancelled',            // cancelled — seat is freed
]);
export type InscriptionStatus = z.infer<typeof inscriptionStatusSchema>;

// Education status reported by the candidate at signup.
// `autre` is retained in the enum so that historical inscriptions remain
// parseable, but it is no longer exposed in the public form (MTC trains
// dentists exclusively — hygiénistes / prothésistes don't apply).
export const educationStatusSchema = z.enum([
  'dentiste',
  'jeune-diplome',
  'etudiant-these',
  'autre',
]);
export type EducationStatus = z.infer<typeof educationStatusSchema>;

// Payment preference declared by the candidate on the public form.
// Admin-side `paymentArrangement` (which adds `invite` / `remise`) remains
// the source of truth for billing — this is the candidate's intent.
export const paymentPreferenceSchema = z.enum([
  'virement',
  'cheque',
  'espece',
  'installments-2',
  'installments-3',
  /** Paid at venue — used by event registrations (paiement sur place).
   *  Inscription is created with empty `payments[]`; admin records the payment
   *  on event day. Not currently offered on the public formation form. */
  'sur-place',
]);
export type PaymentPreference = z.infer<typeof paymentPreferenceSchema>;

// Overall payment arrangement (admin-managed). `invite` = free invitation,
// `remise` = discount applied to the formation price.
// `installments` = échelonné en N parties (N stored in `installmentsCount`).
// The legacy `installments-2` / `installments-3` values are kept so historical
// docs still parse; the admin UI now offers a single « Parties » option with a
// free count and migrates legacy docs to `installments` on next save.
export const paymentArrangementSchema = z.enum([
  'full',
  'installments',
  'installments-2',
  'installments-3',
  'invite',
  'remise',
]);
export type PaymentArrangement = z.infer<typeof paymentArrangementSchema>;

// Method for an individual payment record. Installments are tracked by stacking
// multiple records, each with its own method.
export const paymentMethodSchema = z.enum(['virement', 'cheque', 'espece']);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Individual payment record
// ──────────────────────────────────────────────────────────────────────────
export const paymentSchema = z.object({
  id: z.string(),
  amount: z.number().nonnegative(),
  date: isoDateTime,
  method: paymentMethodSchema,
  /** Required when method === 'cheque'; ignored otherwise. */
  chequeNumber: z.string().optional(),
  /** Encashment date for a cheque — when it cleared (or is expected to). */
  chequeCashingDate: isoDateTime.optional(),
  notes: z.string().max(500).optional(),
});
export type Payment = z.infer<typeof paymentSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Profile data collected by the public inscription form
// ──────────────────────────────────────────────────────────────────────────
export const inscriptionProfileSchema = z.object({
  firstName: z.string().min(1).max(80),
  lastName: z.string().min(1).max(80),
  email: z.string().email(),
  // NOTE: phone / birthdate / nationality / educationStatus / cityOfPractice are
  // OPTIONAL at the schema level so that historical alumni imported from the
  // legacy spreadsheet (which only reliably has name + email) parse cleanly and
  // the member completes the rest later. The PUBLIC inscription form still
  // collects + validates all of them (client-side `validate()` + the Firestore
  // create rule), so new public submissions remain complete.
  phone: z.string().min(6).max(30).optional(),
  /** YYYY-MM-DD (HTML date input format). */
  birthdate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date au format YYYY-MM-DD requise').optional(),
  nationality: z.string().min(1).max(60).optional(),
  educationStatus: educationStatusSchema.optional(),
  /** Number of years practicing — `0` for students. */
  yearsOfPractice: z.number().int().nonnegative().max(70).optional(),
  /** Clinic / practice name. Empty string when not applicable. */
  cabinet: z.string().max(160).optional(),
  /** City where the candidate exercises (different from their home city). */
  cityOfPractice: z.string().min(1).max(80).optional(),
  /** Candidate's declared payment preference. Admin confirms final arrangement later.
   *  Optional in the schema so that pre-launch inscriptions remain parseable; the
   *  public form enforces it at submit time. */
  paymentPreference: paymentPreferenceSchema.optional(),
  newsletter: z.boolean().optional(),
  /** Free-form notes from the candidate. */
  notes: z.string().max(1000).optional(),
  // ────────────────────────────────────────────────────────────────────────
  // Admin-only demographic fields. Captured by the alumni import (xlsx) and
  // editable in admin InscriptionDetail. NOT collected on the public form
  // yet — kept optional so existing docs (and future public signups) parse
  // cleanly without these set.
  // ────────────────────────────────────────────────────────────────────────
  /** Year the candidate obtained their dental doctorate (e.g. 2018). */
  graduationYear: z.number().int().min(1950).max(2100).optional(),
  /** Faculty / school where the doctorate was obtained. Free text. */
  faculty: z.string().max(200).optional(),
  /** Other diplomas and certificates held by the candidate. Free text. */
  otherCredentials: z.string().max(500).optional(),
});
export type InscriptionProfile = z.infer<typeof inscriptionProfileSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Public create payload — what the inscription form submits
// ──────────────────────────────────────────────────────────────────────────
export const inscriptionCreateSchema = z.object({
  formationId: z.string().min(1),
  sessionId: z.string().min(1),
  profile: inscriptionProfileSchema,
});
export type InscriptionCreate = z.infer<typeof inscriptionCreateSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Full inscription doc (admin-visible)
// ──────────────────────────────────────────────────────────────────────────
export const inscriptionSchema = inscriptionCreateSchema.extend({
  id: z.string(),
  status: inscriptionStatusSchema,
  paymentArrangement: paymentArrangementSchema.nullable().default(null),
  /** Number of installments when `paymentArrangement === 'installments'` (2..24).
   *  Null for every other arrangement. */
  installmentsCount: z.number().int().min(2).max(24).nullable().default(null),
  /** Discount applied to the formation price (in MAD). 0 when not applicable. */
  discount: z.number().nonnegative().default(0),
  payments: z.array(paymentSchema).default([]),
  /** Assigned by admin after confirmation. */
  group: z.string().nullable().default(null),
  /** Cohort/promotion label (e.g. "Promotion 2026"). */
  cohort: z.string().nullable().default(null),
  /** Internal admin notes. */
  adminNotes: z.string().max(2000).optional(),
  /** Firebase Auth UID of the member who owns this inscription. Set at write
   *  time when the candidate is signed in + verified, or retroactively by
   *  the `linkMemberInscriptions` Cloud Function once a member verifies an
   *  email that matches `profile.email`. Null for guest inscriptions. */
  memberUid: z.string().nullable().default(null),
  /** Download URL of the participant's signed certificate PDF — uploaded by
   *  admin once the formation is completed. Surfaced in « Mes inscriptions »
   *  for members to download anytime. Null until the admin uploads it. */
  certificateUrl: z.string().url().nullable().default(null),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type Inscription = z.infer<typeof inscriptionSchema>;
